<script setup>
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useStore } from 'vuex';
import UiBadge from '@/components/ui/UiBadge.vue';
import UiFormDialog from '@/components/ui/UiFormDialog.vue';
import UiTableCard from '@/components/ui/UiTableCard.vue';
import { apiRequest } from '@/api/http';
import { useAsyncAction } from '@/composables/useAsyncAction';
import { useConfirm } from '@/composables/useConfirm';
import { useListResource } from '@/composables/useListResource';
import { useSessionGuard } from '@/composables/useSessionGuard';
import { formatDate, isoDate, titleCase } from '@/utils/format';
import { RoleCode } from '@/constants/accounts';

useSessionGuard();

const headers = [
  { title: 'Event', key: 'eventName' },
  { title: 'Added by', key: 'name' },
  { title: 'Date', key: 'eventDate' },
  { title: 'Status', key: 'isActive' },
];

const store = useStore();
const year = computed(() => store.getters.getFilterSelected);

const { items, loading, error, load } = useListResource(async () => {
  const rows = await apiRequest('get', `/school-events/getAllEvents/${year.value}`);
  return (rows || []).map((r) => ({ ...r, name: titleCase(r.name) }));
});
watch(year, load, { immediate: true });

// Events are on the public landing page, so only the superadmin and the
// JHS/SHS Admins add, edit or delete them (the server enforces it too);
// everyone else views.
const EVENT_MANAGERS = [RoleCode.Superadmin, RoleCode.JhsAdmin, RoleCode.ShsAdmin];
const isManager = computed(() => EVENT_MANAGERS.includes(store.getters.activeRoleCode));
const canManage = () => isManager.value;
// Past events stay on record; only upcoming ones can be removed.
const isUpcoming = (event) => new Date() <= new Date(event.eventDate);

function rowActions(event) {
  if (!canManage()) {
    return [{ title: 'View details', icon: 'mdi-file-document-outline', onClick: () => openForm(event) }];
  }
  return [
    { title: 'Edit', icon: 'mdi-pencil-outline', onClick: () => openForm(event) },
    { title: 'Delete', icon: 'mdi-trash-can-outline', destructive: true, hidden: !isUpcoming(event), onClick: () => remove(event) },
  ];
}

// Add / edit / view dialog. The image is optional; choosing one switches
// to the multipart endpoints.
const editing = shallowRef(null); // null = add
const formOpen = shallowRef(false);
const form = ref({ eventName: '', eventDate: '', isActive: 1, eventDescription: '', file: null });
const readonly = computed(() => !!editing.value && !canManage());
// Beside Date: the image when adding an event, the status when editing one
// (the image then gets its own row under the description).
const imageField = computed(() => ({
  key: 'file',
  label: 'Image',
  type: 'file',
  accept: '.png,.jpg,.jpeg',
  description: 'Optional. A landscape PNG or JPG works best.',
  placeholder: editing.value?.eventFile ? 'Replace the current image' : 'Choose an image',
  span: editing.value ? 2 : 1,
  hidden: readonly.value,
}));
const fields = computed(() => [
  { key: 'eventName', label: 'Event name', placeholder: 'e.g. Foundation Day', required: true, span: 2 },
  { key: 'eventDate', label: 'Date', type: 'date', required: true, min: editing.value ? undefined : isoDate(new Date()) },
  ...(editing.value
    ? [
        {
          key: 'isActive',
          label: 'Status',
          type: 'select',
          placeholder: 'Choose a status',
          items: [
            { title: 'Active', value: 1 },
            { title: 'Inactive', value: 0 },
          ],
        },
      ]
    : [imageField.value]),
  {
    key: 'eventDescription',
    label: 'Description',
    type: 'textarea',
    placeholder: 'What is happening, and who should attend',
    required: true,
    span: 2,
  },
  ...(editing.value ? [imageField.value] : []),
]);
const eventImageUrl = (file) => (file ? `${process.env.VUE_APP_SERVER}/school-events/getEventImg/${file}` : '');
const imageUrl = computed(() => eventImageUrl(editing.value?.eventFile));

// The dialog previews the image just chosen, else the one already saved.
const picked = computed(() => (Array.isArray(form.value.file) ? form.value.file[0] : form.value.file) || null);
const pickedUrl = shallowRef('');
watch(picked, (f) => {
  if (pickedUrl.value) URL.revokeObjectURL(pickedUrl.value);
  pickedUrl.value = f ? URL.createObjectURL(f) : '';
});
onBeforeUnmount(() => pickedUrl.value && URL.revokeObjectURL(pickedUrl.value));
const previewUrl = computed(() => pickedUrl.value || imageUrl.value);

function openForm(event = null) {
  editing.value = event;
  form.value = {
    eventName: event?.eventName ?? '',
    eventDate: isoDate(event?.eventDate),
    isActive: event ? Number(event.isActive) : 1,
    eventDescription: event?.eventDescription ?? '',
    file: null,
  };
  formOpen.value = true;
}

const { busy, run } = useAsyncAction();

function multipart(file, data) {
  const fd = new FormData();
  fd.append('file', file);
  fd.append('body', JSON.stringify(data));
  return fd;
}

async function save() {
  const values = form.value;
  const file = Array.isArray(values.file) ? values.file[0] : values.file;
  const event = editing.value;
  const base = {
    eventName: values.eventName.trim(),
    eventDate: values.eventDate,
    eventDescription: values.eventDescription.trim(),
  };
  const request = event
    ? () => {
        const data = { ...base, id: event.id, isActive: values.isActive };
        return file
          ? apiRequest('post', '/school-events/update-event-file', multipart(file, data))
          : apiRequest('patch', `/school-events/${event.id}`, data);
      }
    : () => {
        const data = { ...base, schoolYearID: year.value };
        return file
          ? apiRequest('post', '/school-events/addEvent', multipart(file, data))
          : apiRequest('post', '/school-events', data);
      };
  const ok = await run(request, {
    success: event ? 'Event updated.' : 'Event added.',
    error: 'Could not save the event.',
  });
  if (ok) {
    formOpen.value = false;
    load();
  }
}

const { confirm } = useConfirm();

async function remove(event) {
  const ok = await confirm({
    title: 'Delete event?',
    description: `${event.eventName} will be removed. This can't be undone.`,
    confirmText: 'Delete',
    destructive: true,
  });
  if (ok && (await run(() => apiRequest('delete', `/school-events/${event.id}`), { success: 'Event deleted.', error: 'Could not delete the event.' }))) {
    load();
  }
}
</script>

<template>
  <UiTableCard
    title="Events"
    description="School events for the selected school year."
    :headers="headers"
    :items="items"
    :loading="loading"
    :error="error"
    search-placeholder="Search events"
    :add-label="isManager ? 'Add academic event' : ''"
    empty-text="No events yet."
    row-label="eventName"
    :row-actions="rowActions"
    @add="openForm()"
  >
    <template #[`item.eventName`]="{ item }">
      <span class="event-name">
        <span class="event-thumb">
          <img v-if="item.eventFile" :src="eventImageUrl(item.eventFile)" alt="" loading="lazy" />
          <v-icon v-else size="18">mdi-calendar-star-outline</v-icon>
        </span>
        <span class="font-weight-medium">{{ item.eventName }}</span>
      </span>
    </template>
    <template #[`item.eventDate`]="{ item }">
      {{ formatDate(item.eventDate) }}
    </template>
    <template #[`item.isActive`]="{ item }">
      <UiBadge :variant="item.isActive == 1 ? 'green' : 'secondary'">
        {{ item.isActive == 1 ? 'Active' : 'Inactive' }}
      </UiBadge>
    </template>
  </UiTableCard>

  <UiFormDialog
    v-model="formOpen"
    :title="readonly ? editing.eventName : editing ? 'Edit event' : 'Add academic event'"
    :description="readonly ? formatDate(editing.eventDate) : 'Events are shown to everyone, including on the public landing page.'"
    size="lg"
    layout="grid"
    :fields="fields"
    v-model:values="form"
    :readonly="readonly"
    :saving="busy"
    :submit-text="editing ? 'Save changes' : 'Add event'"
    @submit="save"
  >
    <img v-if="previewUrl" :src="previewUrl" alt="" class="event-preview span-2" />
    <template v-if="imageUrl" #footer-start>
      <v-btn variant="text" prepend-icon="mdi-image-outline" :href="imageUrl" target="_blank" class="mr-auto">
        View image
      </v-btn>
    </template>
  </UiFormDialog>
</template>

<style scoped>
.event-name {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.event-thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--muted-foreground);
}
.event-thumb img,
.event-preview {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.event-preview {
  height: auto;
  max-height: 260px;
  border: 1px solid var(--border);
  border-radius: 10px;
}
</style>
